import { useState, useRef, useEffect } from "react";
import { Check, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DirtyStateBannerProps {
  pendingChangeCount: number;
  hasPendingChanges: boolean;
  isCommitting: boolean;
  isDiscarding: boolean;
  isSyncing: boolean;
  isOutOfDate: boolean;
  isConflicting: boolean;
  isIntegrationBlocked: boolean;
  isResolvingConflicts: boolean;
  canPublish: boolean;
  canDiscardToRemote: boolean;
  providerLabel?: string;
  onCommit: () => void;
  onDiscardAll: () => void;
}

// ---------------------------------------------------------------------------
// Animated wrapper — collapses height + fades when hidden
// ---------------------------------------------------------------------------

function AnimatedBanner({
  visible,
  children,
}: {
  visible: boolean;
  children: React.ReactNode;
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (visible && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    } else {
      setHeight(0);
    }
  }, [visible]);

  // Re-measure when children change (e.g. count updates)
  useEffect(() => {
    if (visible && contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
  });

  return (
    <div
      className="overflow-hidden transition-all duration-200 ease-in-out"
      style={{
        maxHeight: height,
        opacity: visible ? 1 : 0,
      }}
    >
      <div ref={contentRef}>{children}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desktop: inline indicator that sits in the breadcrumbs bar
// ---------------------------------------------------------------------------

export function DirtyStateInline({
  pendingChangeCount,
  hasPendingChanges,
  isCommitting,
  isDiscarding,
  isSyncing,
  isOutOfDate,
  isConflicting,
  isIntegrationBlocked,
  isResolvingConflicts,
  canPublish,
  canDiscardToRemote,
  providerLabel = "remote",
  onCommit,
  onDiscardAll,
}: DirtyStateBannerProps) {
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  useEffect(() => {
    if (!hasPendingChanges) {
      setShowDiscardDialog(false);
    }
  }, [hasPendingChanges]);

  if (!hasPendingChanges) return null;

  const commitDisabled =
    !canPublish ||
    !hasPendingChanges ||
    isCommitting ||
    isDiscarding ||
    isResolvingConflicts ||
    isOutOfDate ||
    isConflicting ||
    isIntegrationBlocked;

  const commitTooltip = isOutOfDate
    ? "Sync required before committing"
    : isConflicting
      ? "Resolve conflicts before committing"
      : isIntegrationBlocked
        ? `Reconnect ${providerLabel} integration to commit`
        : !canPublish
          ? "This project source does not support publishing"
          : undefined;

  return (
    <>
      <div className="flex items-center gap-2 rounded-md bg-amber-100/70 px-2.5 py-1 text-xs dark:bg-amber-500/15">
        {/* Amber dot */}
        <span className="relative flex size-2">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber-400 opacity-75" />
          <span className="relative inline-flex size-2 rounded-full bg-amber-500" />
        </span>

        <span className="text-amber-900 dark:text-amber-200 mr-4">
          {pendingChangeCount} file{pendingChangeCount === 1 ? "" : "s"} changed
        </span>

        {/* Discard first (secondary), then Commit (primary) */}
        <Button
          variant="outline"
          size="xs"
          className="h-6 gap-1 text-amber-800 dark:text-amber-200"
          onClick={() => setShowDiscardDialog(true)}
          disabled={isDiscarding || isCommitting || !canDiscardToRemote}
        >
          <Undo2 className="size-3" />
          {isDiscarding ? "Discarding..." : "Discard"}
        </Button>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="default"
              size="xs"
              className=" gap-1"
              onClick={onCommit}
              disabled={commitDisabled}
            >
              <Check className="size-3" />
              {isCommitting ? "Committing..." : "Commit"}
            </Button>
          </TooltipTrigger>
          {commitTooltip && <TooltipContent>{commitTooltip}</TooltipContent>}
        </Tooltip>
      </div>

      <DiscardConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onConfirm={onDiscardAll}
        count={pendingChangeCount}
        isDiscarding={isDiscarding}
        hasPendingChanges={hasPendingChanges}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Mobile: full-width bar that sits below the toolbar
// ---------------------------------------------------------------------------

export function DirtyStateMobileBanner({
  pendingChangeCount,
  hasPendingChanges,
  isCommitting,
  isDiscarding,
  isSyncing,
  isOutOfDate,
  isConflicting,
  isIntegrationBlocked,
  isResolvingConflicts,
  canPublish,
  canDiscardToRemote,
  providerLabel = "remote",
  onCommit,
  onDiscardAll,
}: DirtyStateBannerProps) {
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const commitDisabled =
    !canPublish ||
    !hasPendingChanges ||
    isCommitting ||
    isDiscarding ||
    isResolvingConflicts ||
    isOutOfDate ||
    isConflicting ||
    isIntegrationBlocked;

  return (
    <>
      <AnimatedBanner visible={hasPendingChanges}>
        <div className="flex items-center justify-between gap-2 border-b bg-amber-50/80 px-3 py-1.5 dark:bg-amber-500/10">
          <div className="flex items-center gap-1.5 text-xs">
            {/* Amber dot */}
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex size-2 rounded-full bg-amber-500" />
            </span>
            <span className="text-amber-900 dark:text-amber-200">
              {pendingChangeCount} file{pendingChangeCount === 1 ? "" : "s"}{" "}
              changed
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="xs"
              className="gap-1 text-amber-800 dark:text-amber-200"
              onClick={() => setShowDiscardDialog(true)}
              disabled={isDiscarding || isCommitting || !canDiscardToRemote}
            >
              <Undo2 className="size-3" />
              {isDiscarding ? "Discarding..." : "Discard"}
            </Button>
            <Button
              variant="default"
              size="xs"
              className="gap-1"
              onClick={onCommit}
              disabled={commitDisabled}
            >
              <Check className="size-3" />
              {isCommitting ? "Committing..." : "Commit"}
            </Button>
          </div>
        </div>
      </AnimatedBanner>

      <DiscardConfirmDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onConfirm={onDiscardAll}
        count={pendingChangeCount}
        isDiscarding={isDiscarding}
        hasPendingChanges={hasPendingChanges}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Discard confirmation alert dialog
// ---------------------------------------------------------------------------

function DiscardConfirmDialog({
  open,
  onOpenChange,
  onConfirm,
  count,
  isDiscarding,
  hasPendingChanges,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  count: number;
  isDiscarding: boolean;
  hasPendingChanges: boolean;
}) {
  const discardTriggeredRef = useRef(false);

  // Reset when dialog closes
  useEffect(() => {
    if (!open) discardTriggeredRef.current = false;
  }, [open]);

  // Close dialog as soon as the revert is visible (hasPendingChanges becomes false).
  // The discard action continues in the background (getStatus, emitStatusEvents) for
  // several more seconds, but the files are already updated—no need to keep the dialog open.
  useEffect(() => {
    if (discardTriggeredRef.current && !hasPendingChanges) {
      discardTriggeredRef.current = false;
      onOpenChange(false);
    }
  }, [hasPendingChanges, onOpenChange]);

  const handleConfirm = () => {
    discardTriggeredRef.current = true;
    onConfirm();
    // Keep dialog open — will close in useEffect when isDiscarding becomes false
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard all changes?</AlertDialogTitle>
          <AlertDialogDescription>
            This will revert {count} changed file{count === 1 ? "" : "s"} back
            to the last committed state. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDiscarding}>Cancel</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isDiscarding}
          >
            {isDiscarding ? "Discarding..." : "Discard All"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
